// 갤러리 탭 맨 위의 카드 더미 (2.4.1~, drape Trends 의 brief 카드와 같은 모양).
// 캐러셀이 아니라 더미 — 맨 위 한 장 아래로 나머지 가장자리가 비친다. ✕ 나 옆으로 던지면
// 닫히고, 누르면 그 카드의 일을 한다 (본문이 있는 문서 카드는 먼저 시트로 펼친다).
// 닫은 카드는 이 기기에서 안 뜬다 — reshowDays 가 있으면 그 뒤에 다시.
//
// 카드는 두 갈래:
//   앱이 만드는 카드  — 지금 시즌 스타일 / 크레딧이 다음 한 장에 모자람 / 친구 초대.
//   문서 카드        — config/galleryBriefs (functions/gallery-briefs.js). 영어만 쓰면 번역된다.
// 초대 카드는 "초대하면 둘 다 받는다" 만 말한다 — 코드로 크레딧을 받는다는 말은 iOS 에서 금지 (3.1.1).
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../hooks/useLocale.jsx';
import { AIService } from '../services/ai-service.js';
import { useCredits, useCreditConfig } from '../services/credits-service.js';
import { briefText, closeBrief, isBriefClosed, liveRemoteBriefs, loadGalleryBriefs } from '../services/gallery-briefs.js';
import { styleThumb, styleThumbFallback } from '../utils/style-thumb.js';
import { logEvent, analytics } from '../firebase.js';

const THROW_PX = 90;   // 이만큼 끌면 던져서 닫는다
const THROW_MS = 220;
const STEP = 10;       // 아래 카드가 비치는 폭

function builtinBriefs({ t, styleName, category, credits, cfg }) {
  const out = [];
  const signedIn = !credits.isGuest;
  const price = cfg?.price?.generate || 10;

  // 다음 한 장에 모자랄 때만. 일일분은 내일 다시 차니 그 말도 같이.
  if (signedIn && !credits.loading && credits.credits != null && credits.credits < price) {
    out.push({
      id: 'topup', reshowDays: 1, icon: 'bolt', to: 'credits',
      tx: { kicker: t('briefTopupKicker'), title: t('briefTopupTitle'), line: t('briefTopupLine') },
    });
  }

  // 지금 창이 열린 시즌 스타일. 해마다 다시 뜨도록 id 에 연도를 붙인다.
  const year = new Date().getFullYear();
  Object.entries(AIService.styleCatalog(category))
    .filter(([, v]) => v.season)
    .forEach(([key]) => {
      const name = styleName(key, category);
      out.push({
        id: `season_${key}_${year}`, image: styleThumb(category, key), imageFallback: styleThumbFallback(category, key),
        to: { style: key, category },
        tx: { kicker: t('briefSeasonKicker'), title: t('briefSeasonTitle', { name }), line: t('briefSeasonLine') },
      });
    });

  if (signedIn) {
    out.push({
      id: 'invite', reshowDays: 14, icon: 'group_add', to: 'invite', last: true,
      tx: { kicker: t('briefInviteKicker'), title: t('briefInviteTitle', { n: cfg?.inviteReward ?? '' }), line: t('briefInviteLine') },
    });
  }
  return out;
}

function BriefSheet({ brief, onAct, onClose }) {
  const { t } = useLocale();
  const tx = brief.tx;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card gbrief-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button type="button" className="modal-close-btn" onClick={onClose} aria-label={t('close')}>
          <i className="material-symbols-rounded">close</i>
        </button>
        {brief.image && <img className="gbrief-sheet-img" src={brief.image} alt="" />}
        {tx.kicker && <p className="gbrief-kicker">{tx.kicker}</p>}
        <h3 className="gbrief-sheet-title">{tx.title}</h3>
        {(tx.body || tx.line) && <div className="gbrief-sheet-body">{tx.body || tx.line}</div>}
        {brief.to && tx.cta && (
          <button type="button" className="btn btn-primary gbrief-sheet-cta" onClick={onAct}>{tx.cta}</button>
        )}
      </div>
    </div>
  );
}

export function GalleryBriefs({ category, onCreditsClick }) {
  const { t, lang, styleName } = useLocale();
  const navigate = useNavigate();
  const credits = useCredits();
  const cfg = useCreditConfig();
  const [remote, setRemote] = useState([]);
  const [gone, setGone] = useState(() => new Set());
  const [open, setOpen] = useState(null);
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const [throwing, setThrowing] = useState(null);

  useEffect(() => {
    let stale = false;
    loadGalleryBriefs().then((l) => { if (!stale) setRemote(l); });
    return () => { stale = true; };
  }, []);

  const signedIn = !credits.isGuest;
  const built = builtinBriefs({ t, styleName, category, credits, cfg });
  const docs = liveRemoteBriefs(remote, { signedIn }).map((b) => ({ ...b, tx: briefText(b, lang) }));
  const cards = [
    ...built.filter((b) => !b.last),
    ...docs,
    ...built.filter((b) => b.last),
  ].filter((b) => !gone.has(b.id) && !isBriefClosed(b));
  if (!cards.length) return null;
  const total = cards.length + gone.size;
  const top = cards[0];

  const act = (b) => {
    logEvent(analytics, 'brief_action', { id: b.id });
    setOpen(null);
    const to = b.to;
    if (to === 'credits') onCreditsClick?.();
    else if (to === 'invite') navigate('/invite');
    else if (to && typeof to === 'object') {
      navigate('/create', { state: { spaceCategory: to.category, style: to.style, fromGallery: true } });
    } else if (typeof to === 'string' && to.startsWith('/')) navigate(to);
    else if (typeof to === 'string' && /^https:\/\//.test(to)) window.open(to, '_blank', 'noopener');
  };

  const dismiss = (id, dir = 1) => {
    if (throwing) return;
    setThrowing({ id, dir });
    closeBrief(id);
    logEvent(analytics, 'brief_close', { id });
    setTimeout(() => {
      setGone((g) => new Set(g).add(id));
      setThrowing(null);
      setDrag(null);
      dragRef.current = null;
    }, THROW_MS);
  };

  // 끄는 중의 값은 ref 에 — pointerup 이 React 가 아직 안 그린 마지막 움직임도 봐야 한다.
  const onDown = (e) => {
    if (throwing) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { x0: e.clientX, dx: 0, moved: false };
    setDrag({ dx: 0, live: true });
  };
  const onMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    d.dx = e.clientX - d.x0;
    d.moved = d.moved || Math.abs(d.dx) > 6;
    setDrag({ dx: d.dx, live: true });
  };
  const onUp = () => {
    const d = dragRef.current;
    if (!d) return;
    if (Math.abs(d.dx) > THROW_PX) dismiss(top.id, Math.sign(d.dx));
    else setDrag({ dx: 0, live: false });
  };
  const onTap = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d?.moved) return;
    logEvent(analytics, 'brief_open', { id: top.id });
    // 본문이 따로 있는 카드만 시트로 펼친다. 나머지는 바로 그 일을 한다.
    if (top.tx.body) setOpen(top);
    else act(top);
  };

  const dx = throwing ? throwing.dir * 480 : (drag?.dx || 0);
  const style = {
    transform: `translateX(${dx}px) rotate(${dx / 40}deg)`,
    opacity: throwing ? 0 : 1,
    transition: drag?.live && !throwing ? 'none' : `transform ${THROW_MS}ms ease-out, opacity ${THROW_MS}ms ease-out`,
  };
  const behind = Math.min(cards.length - 1, 2);
  const tx = top.tx;

  return (
    <section className="gbriefs" aria-label={t('briefsLabel')}>
      <div className="gbriefs-stack" style={{ paddingBottom: behind * STEP }}>
        {Array.from({ length: behind }, (_, i) => (
          <div key={i} className="gbrief gbrief--under" aria-hidden="true"
            style={{ bottom: behind * STEP, transform: `translateY(${(i + 1) * STEP}px) scale(${1 - (i + 1) * 0.045})`, zIndex: 2 - i }} />
        ))}
        <div
          key={top.id}
          role="button"
          tabIndex={0}
          className="gbrief"
          style={{ ...style, zIndex: 3 }}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onClick={onTap}
          onKeyDown={(e) => { if (e.key === 'Enter') onTap(); }}
        >
          {top.image
            ? <img className="gbrief-img" src={top.image} onError={top.imageFallback} alt="" draggable={false} />
            : top.icon && <span className="gbrief-icon" aria-hidden="true"><i className="material-symbols-rounded">{top.icon}</i></span>}
          <div className="gbrief-text">
            {tx.kicker && <span className="gbrief-kicker">{tx.kicker}</span>}
            <span className="gbrief-title">{tx.title}</span>
            {tx.line && <span className="gbrief-line">{tx.line}</span>}
          </div>
          <button
            type="button"
            className="gbrief-x"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); dismiss(top.id); }}
            aria-label={t('close')}
          >
            <i className="material-symbols-rounded">close</i>
          </button>
          {total > 1 && <span className="gbrief-count">{gone.size + 1}/{total}</span>}
        </div>
      </div>
      {open && <BriefSheet brief={open} onAct={() => act(open)} onClose={() => setOpen(null)} />}
    </section>
  );
}

export default GalleryBriefs;
